import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(private messageService: MessageService) {}

  /** Suppresses an identical toast while an earlier one is still on screen —
   *  double navigations (guards redirecting, menu commands re-firing) otherwise
   *  stack the same message twice. Keyed by severity+title+message, expiring
   *  after the toast's own `life`. */
  private readonly recent = new Map<string, number>();
  private isDuplicate(key: string, life: number): boolean {
    const now = Date.now();
    const until = this.recent.get(key);
    if (until && until > now) return true;
    this.recent.set(key, now + life);
    return false;
  }

  showError(message: string, title: string = 'Error') {
    if (this.isDuplicate(`error|${title}|${message}`, 5000)) return;
    this.messageService.add({
      styleClass: 'custom-toast error-toast shadow-lg rounded-xl border-left-4 border-red-600',
      severity: 'error',
      summary: title,
      detail: message,
      life: 5000,
      closable: true,
      icon: 'pi pi-exclamation-triangle text-xl text-red-600',
      contentStyleClass: 'error-content font-small',
      // key: 'main-toast',
      // sticky: true,
      closeIcon: 'pi pi-times text-red-400 hover:text-red-600 transition-colors duration-200'
    });
  }

  showSuccess(message: string, title: string = 'Success') {
    if (this.isDuplicate(`success|${title}|${message}`, 3000)) return;
    this.messageService.add({
      styleClass: 'custom-toast success-toast shadow-lg rounded-xl border-left-4 border-green-600',
      severity: 'success',
      summary: title,
      detail: message,
      life: 3000,
      closable: true,
      icon: 'pi pi-check-circle text-xl text-green-600',
      contentStyleClass: 'font-medium mt-1',
      closeIcon: 'pi pi-times text-green-400 hover:text-green-600 transition-colors duration-200'
    });
  }

  showInfo(message: string, title: string = 'Information') {
    if (this.isDuplicate(`info|${title}|${message}`, 3000)) return;
    this.messageService.add({
      styleClass: 'custom-toast info-toast shadow-lg rounded-xl border-left-4 border-blue-600',
      severity: 'info',
      summary: title,
      detail: message,
      life: 3000,
      closable: true,
      icon: 'pi pi-info-circle text-xl text-blue-600',
      contentStyleClass: 'font-medium mt-1',
      closeIcon: 'pi pi-times text-blue-400 hover:text-blue-600 transition-colors duration-200'
    });
  }

  showWarning(message: string, title: string = 'Warning') {
    if (this.isDuplicate(`warn|${title}|${message}`, 4000)) return;
    this.messageService.add({
      styleClass: 'custom-toast warning-toast shadow-lg rounded-xl border-left-4 border-amber-600',
      severity: 'warn',
      summary: title,
      detail: message,
      life: 4000,
      closable: true,
      icon: 'pi pi-exclamation-circle text-xl text-amber-600',
      contentStyleClass: 'font-medium mt-1',
      closeIcon: 'pi pi-times text-amber-400 hover:text-amber-600 transition-colors duration-200'
    });
  }
}
